import { useCallback, useMemo } from 'react';
import { useUserPreferences } from '@/common/application/hooks/userPreferencesHook';
import type { UserPreferences } from '@/store/UserPreferencesStore';

/**
 * Preference key for optional field visibility on the Create Job form.
 * Used only in create mode; edit mode always derives visibility from form values.
 */
export const CREATE_JOB_OPTIONAL_FIELDS_VISIBILITY_KEY = 'createJobOptionalFieldsVisibility';

/**
 * Preference key for optional field values on the Create Job form.
 * Persisted when the modal is closed so values are restored on next open.
 */
export const CREATE_JOB_OPTIONAL_FIELDS_VALUES_KEY = 'createJobOptionalFieldsValues';

export type CreateJobOptionalFieldsVisibility = {
  showVendor?: boolean;
  showShowUpTime?: boolean;
  showLeaveByTime?: boolean;
  showOnJobTime?: boolean;
  showPONumber?: boolean;
  showJobEndDate?: boolean;
  showJobEndTime?: boolean;
};

/**
 * Stored representation of optional field values.
 * Times are stored as "HH:mm:ss" strings; restored to Date in the form.
 * vendorName is stored so the Vendor autocomplete can display the selected option before the vendors list loads.
 */
export type CreateJobOptionalFieldsValues = {
  vendorId?: number | null;
  vendorName?: string | null;
  showUpTime?: string | null;
  leaveByTime?: string | null;
  onJobTime?: string | null;
  poNumber?: string | null;
};

const DEFAULT_VISIBILITY: CreateJobOptionalFieldsVisibility = {
  showVendor: false,
  showShowUpTime: false,
  showLeaveByTime: false,
  showOnJobTime: false,
  showPONumber: false,
  showJobEndDate: false,
  showJobEndTime: false
};

function mergeWithDefaults(stored: CreateJobOptionalFieldsVisibility | undefined): Required<CreateJobOptionalFieldsVisibility> {
  if (!stored || typeof stored !== 'object') {
    return { ...DEFAULT_VISIBILITY } as Required<CreateJobOptionalFieldsVisibility>;
  }
  return {
    showVendor: stored.showVendor ?? false,
    showShowUpTime: stored.showShowUpTime ?? false,
    showLeaveByTime: stored.showLeaveByTime ?? false,
    showOnJobTime: stored.showOnJobTime ?? false,
    showPONumber: stored.showPONumber ?? false,
    showJobEndDate: stored.showJobEndDate ?? false,
    showJobEndTime: stored.showJobEndTime ?? false
  };
}

/**
 * Hook to read and persist optional field visibility and values for the Create Job form.
 * Persistence is per-user (UserPreferencesStore) and applies across sessions.
 * Use only when mode === 'create'; edit mode should use form values for initial state.
 */
export function useCreateJobOptionalFieldsPreferences() {
  const { preferences, setPreference } = useUserPreferences();

  const stored = preferences[CREATE_JOB_OPTIONAL_FIELDS_VISIBILITY_KEY] as CreateJobOptionalFieldsVisibility | undefined;

  const visibility = useMemo(() => mergeWithDefaults(stored), [stored]);

  const setFieldVisible = useCallback(
    (field: keyof CreateJobOptionalFieldsVisibility, visible: boolean) => {
      const next: CreateJobOptionalFieldsVisibility = {
        ...mergeWithDefaults(stored),
        [field]: visible
      };
      setPreference(CREATE_JOB_OPTIONAL_FIELDS_VISIBILITY_KEY, next);
    },
    [stored, setPreference]
  );

  const savedValues = useMemo(
    () => (preferences[CREATE_JOB_OPTIONAL_FIELDS_VALUES_KEY] as CreateJobOptionalFieldsValues | undefined) ?? {},
    [preferences]
  );

  const setSavedValues = useCallback(
    (values: CreateJobOptionalFieldsValues) => {
      setPreference(CREATE_JOB_OPTIONAL_FIELDS_VALUES_KEY, values as UserPreferences[string]);
    },
    [setPreference]
  );

  const updateSavedValues = useCallback(
    (partial: Partial<CreateJobOptionalFieldsValues>) => {
      const current = (preferences[CREATE_JOB_OPTIONAL_FIELDS_VALUES_KEY] as CreateJobOptionalFieldsValues | undefined) ?? {};
      setPreference(CREATE_JOB_OPTIONAL_FIELDS_VALUES_KEY, { ...current, ...partial } as UserPreferences[string]);
    },
    [preferences, setPreference]
  );

  return { visibility, setFieldVisible, savedValues, setSavedValues, updateSavedValues };
}
